import { create } from "zustand";

type RuntimeView = 'task_chain' | 'template' | 'timeline' | 'queue';
type RuntimeViewInput = RuntimeView | 'batch';

interface DataFlowState {
  // Main tabs
  activeTab: 'runtime' | 'templates' | 'dataview';
  setActiveTab: (tab: 'runtime' | 'templates' | 'dataview') => void;

  // Runtime sub-view
  runtimeView: RuntimeView;
  setRuntimeView: (view: RuntimeViewInput) => void;

  // Selection state
  selectedTaskChainId: string | null;
  selectedBatchId: string | null;
  selectedInstanceId: number | null;
  selectedTemplateId: string | null;
  setSelectedTaskChain: (id: string | null) => void;
  setSelectedBatch: (id: string | null) => void;
  setSelectedInstance: (id: number | null) => void;
  setSelectedTemplate: (id: string | null) => void;

  // Expansion state for batch tree
  expandedTaskChainIds: Set<string>;
  expandedBatchIds: Set<string>;
  toggleTaskChainExpanded: (id: string) => void;
  toggleBatchExpanded: (id: string) => void;
  setExpandedTaskChainIds: (ids: string[]) => void;
  setExpandedBatchIds: (ids: string[]) => void;
  collapseAllTaskChains: () => void;
  collapseAllBatches: () => void;

  // Instance logs (real-time + historical)
  instanceLogs: Record<number, Array<{ ts: string; level: string; message: string }>>;
  appendInstanceLog: (instanceId: number, log: { ts: string; level: string; message: string }) => void;
  clearInstanceLogs: (instanceId: number) => void;

  // Worker tail logs for bottom bar ticker
  workerLogTail: Array<{ ts: string; level: string; instanceId: number; message: string }>;
  appendWorkerLogTail: (entry: { ts: string; level: string; instanceId: number; message: string }) => void;
  clearWorkerLogTail: () => void;

  // Structured runtime stream for bottom bar (worker-managed FIFO via websocket)
  runtimeStreamEvents: Array<{
    ts: string;
    kind: string;
    phase: string;
    level: string;
    instanceId: number;
    templateId: number;
    batchId: number;
    taskType: string;
    title: string;
    message: string;
    metrics?: Record<string, unknown>;
  }>;
  setRuntimeStreamEvents: (
    events: Array<{
      ts: string;
      kind: string;
      phase: string;
      level: string;
      instanceId: number;
      templateId: number;
      batchId: number;
      taskType: string;
      title: string;
      message: string;
      metrics?: Record<string, unknown>;
    }>
  ) => void;
  appendRuntimeStreamEvent: (entry: {
    ts: string;
    kind: string;
    phase: string;
    level: string;
    instanceId: number;
    templateId: number;
    batchId: number;
    taskType: string;
    title: string;
    message: string;
    metrics?: Record<string, unknown>;
  }) => void;
  runtimeStreaming: boolean;
  runtimeActiveInstances: number;
  runtimeUpdatedAt: string;
  setRuntimeStreamState: (state: { streaming?: boolean; active_instances?: number; updated_at?: string }) => void;
}

export const useDataFlowStore = create<DataFlowState>((set) => ({
  // Main tabs
  activeTab: 'runtime',
  setActiveTab: (activeTab) => set({ activeTab }),

  // Runtime sub-view
  runtimeView: 'task_chain',
  setRuntimeView: (runtimeView) =>
    set({
      runtimeView: runtimeView === "batch" ? "task_chain" : runtimeView,
    }),

  // Selection state
  selectedTaskChainId: null,
  selectedBatchId: null,
  selectedInstanceId: null,
  selectedTemplateId: null,
  setSelectedTaskChain: (selectedTaskChainId) =>
    set({
      selectedTaskChainId,
      selectedBatchId: selectedTaskChainId,
    }),
  setSelectedBatch: (selectedBatchId) =>
    set({
      selectedBatchId,
      selectedTaskChainId: selectedBatchId,
    }),
  setSelectedInstance: (selectedInstanceId) => set({ selectedInstanceId }),
  setSelectedTemplate: (selectedTemplateId) => set({ selectedTemplateId }),

  // Expansion state for batch tree
  expandedTaskChainIds: new Set<string>(),
  expandedBatchIds: new Set<string>(),
  toggleTaskChainExpanded: (id) =>
    set((state) => {
      const newExpanded = new Set(state.expandedTaskChainIds);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return {
        expandedTaskChainIds: newExpanded,
        expandedBatchIds: newExpanded,
      };
    }),
  toggleBatchExpanded: (id) =>
    set((state) => {
      const newExpanded = new Set(state.expandedTaskChainIds);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return {
        expandedTaskChainIds: newExpanded,
        expandedBatchIds: newExpanded,
      };
    }),
  setExpandedTaskChainIds: (ids) =>
    set({
      expandedTaskChainIds: new Set(ids),
      expandedBatchIds: new Set(ids),
    }),
  setExpandedBatchIds: (ids) =>
    set({
      expandedTaskChainIds: new Set(ids),
      expandedBatchIds: new Set(ids),
    }),
  collapseAllTaskChains: () =>
    set({
      expandedTaskChainIds: new Set<string>(),
      expandedBatchIds: new Set<string>(),
    }),
  collapseAllBatches: () =>
    set({
      expandedTaskChainIds: new Set<string>(),
      expandedBatchIds: new Set<string>(),
    }),

  // Instance logs
  instanceLogs: {},
  appendInstanceLog: (instanceId, log) =>
    set((state) => {
      const existing = state.instanceLogs[instanceId] || [];
      const nextLogs = [...existing, log];
      const cappedLogs = nextLogs.length > 500 ? nextLogs.slice(nextLogs.length - 500) : nextLogs;
      return {
        instanceLogs: {
          ...state.instanceLogs,
          [instanceId]: cappedLogs,
        },
      };
    }),
  clearInstanceLogs: (instanceId) =>
    set((state) => {
      const next = { ...state.instanceLogs };
      delete next[instanceId];
      return { instanceLogs: next };
    }),

  workerLogTail: [],
  appendWorkerLogTail: (entry) =>
    set((state) => {
      const next = [...state.workerLogTail, entry];
      const capped = next.length > 300 ? next.slice(next.length - 300) : next;
      return { workerLogTail: capped };
    }),
  clearWorkerLogTail: () => set({ workerLogTail: [] }),

  runtimeStreamEvents: [],
  setRuntimeStreamEvents: (events) =>
    set(() => {
      const capped = events.length > 500 ? events.slice(events.length - 500) : events;
      return { runtimeStreamEvents: capped };
    }),
  appendRuntimeStreamEvent: (entry) =>
    set((state) => {
      const next = [...state.runtimeStreamEvents, entry];
      const capped = next.length > 500 ? next.slice(next.length - 500) : next;
      return { runtimeStreamEvents: capped };
    }),
  runtimeStreaming: false,
  runtimeActiveInstances: 0,
  runtimeUpdatedAt: "",
  setRuntimeStreamState: (state) =>
    set((prev) => ({
      runtimeStreaming:
        typeof state.streaming === "boolean" ? state.streaming : prev.runtimeStreaming,
      runtimeActiveInstances:
        typeof state.active_instances === "number"
          ? state.active_instances
          : prev.runtimeActiveInstances,
      runtimeUpdatedAt:
        typeof state.updated_at === "string" ? state.updated_at : prev.runtimeUpdatedAt,
    })),
}));
